const _ = require("lodash");
const PS = require("./util/ParserState");
const Helpers = require("./util/ParserHelpers");

const noPaddingTypes = ["encircle", "boxed", "fakeblank", "blank"];

const parseOptree = function(
	ps,
	optreeSpecPs,
	parseCellContents,
	extraOptions
) {
	ps = PS.convert(ps); // Environment data
	optreeSpecPs = PS.convert(optreeSpecPs); // Arguments

	if (!extraOptions.raw) {
		const matchBeginning = String.raw`(?:\\\\|^)\s*(?:\\connect(?:\[\w*\])*{[\d;]*}{\d*}\s*)*`;
		// Math mode doesn't work correctly when digits are being separated into different nodes
		// ["4", "-", "2"] makes the minus look like a negative because it doesn't register the full "4 - 2"
		ps.setRest(
			ps.text
				.slice(ps.pos)
				.replace(
					// Add a space before \boxed and \encircled that aren't at the beginning
					new RegExp(
						// This should be negative lookbehind, but only chrome supports it
						String.raw`(${matchBeginning})?(&?\s*\\(${noPaddingTypes.join(
							"|"
						)})\{\s*(\+|-))`,
						"g"
					),
					(match, p1, p2) => {
						if (p1) {
							return match;
						} else {
							return `\\quad[0.15] ${match}`;
						}
					}
				)
				.replace(
					// Handle leading negatives
					new RegExp(`(${matchBeginning})-`, "g"),
					"$1 \\neg "
				)
				.replace(
					// * -, + -, - -, ( -, all imply negative
					/(\+|\-|\()\s*-\s*/g,
					"$1 \\neg "
				)
				// Otherwise, it's a minus
				.replace(/\s*-\s*/g, " \\minus ")
				// Use \plus instead of +
				.replace(/\s*\+\s*/g, " \\plus ")
		);

		let newText = "";
		for (
			let i = ps.pos, braceSum = 0, bracketSum = 0;
			i < ps.text.length;
			i++
		) {
			// If previous characters have closed all braces, add ampersands if character is a number delimiter
			if (braceSum === 0 && bracketSum === 0) {
				const isPrevDigit = i > 0 && /\d/.test(ps.text[i - 1]);
				const isCurrDigit = /\d/.test(ps.text[i]);

				if (isPrevDigit !== isCurrDigit) {
					newText += " & ";
				}
			}

			if (ps.text[i] === "{") {
				braceSum++;
			}
			if (ps.text[i] === "[") {
				bracketSum++;
			}
			if (ps.text[i] === "}") {
				// Putting this line above the ampersand injections above may cause an issue with {1} -> {1&}, so don't move this code
				braceSum--;
			}
			if (ps.text[i] === "]") {
				bracketSum--;
			}

			newText += ps.text[i];
		}

		ps.setRest(newText);
	}

	const optreeRowPs = Helpers.splitByDelimIntoPs(ps, {begin: "\\\\"});
	if (optreeRowPs[optreeRowPs.length - 1].getRest().trim() !== "") {
		ps.pushError("The last optree row must end with \\\\");
	}
	optreeRowPs.pop(); // Ignore empty string after the final \\

	const optreeRowNodes = optreeRowPs.map((rowPs, i) => {
		const row = parseOptreeRow(rowPs, parseCellContents, extraOptions);
		return row;
	});

	// Check that all \connect calls are valid

	if (optreeRowNodes.length && optreeRowNodes[0].connectList.length) {
		ps.pushError("You cannot have a \\connect in the first row");
		optreeRowNodes[0].connectList.forEach((connect, i) => {
			connect.type = "error";
			connect.name = "Connect #" + (i + 1) + " from row #1.";
		});
	}

	const connectArgLimitation = extraOptions.raw
		? "number of nodes"
		: "number of numbers";
	const connectOriginalRowNum = extraOptions.raw
		? i => i + 1
		: i => Math.floor((i - 1) / 2 + 1);

	optreeRowNodes.forEach((rowNode, i) => {
		// Loop over \connect calls and check for correctness
		rowNode.connectList.forEach((connect, j) => {
			if (connect === false) {
				rowNode.connectList[j] = {
					type: "error",
					name: "Connect #" + (j + 1) + " from row #" + (i + 1) + ".",
				};
				return;
			}

			let connectError = false;

			// If currCol is a column that doesn't exist on this rowNode
			if (connect.currCol >= rowNode.children.length) {
				ps.pushError(
					"The second argument of connect #" +
						(j + 1) +
						" in row #" +
						(i + 1) +
						" cannot be greater than the " +
						connectArgLimitation +
						" in the following row. " +
						"(" +
						connectOriginalRowNum(connect.currCol) +
						" cannot be greater than " +
						connectOriginalRowNum(rowNode.children.length - 1) +
						")."
				);
				connectError = true;
			}

			// If some value in prevCols is a column that doesn't exist on the previous rowNode
			connect.prevCols.forEach((prevCol, k) => {
				if (
					i > 0 &&
					optreeRowNodes[i - 1].type !== "error" &&
					prevCol >= optreeRowNodes[i - 1].children.length
				) {
					ps.pushError(
						"Number #" +
							(k + 1) +
							", in the first argument of connect #" +
							(j + 1) +
							" in row #" +
							(i + 1) +
							", cannot be greater than the " +
							connectArgLimitation +
							" in the previous row. " +
							"(" +
							connectOriginalRowNum(prevCol) +
							" cannot be greater than " +
							connectOriginalRowNum(optreeRowNodes[i - 1].children.length - 1) +
							")."
					);
					connectError = true;
				}
			});

			// Prevent drawing that connection, but keep it in the AST
			if (connectError) {
				connect.type = "error";
				connect.name = "Connect #" + (j + 1) + " from row #" + (i + 1) + ".";
			}
		});
	});

	// Done checking \connect calls for validity

	return [
		{
			type: "optree",
			children: optreeRowNodes,
			extraOptions: extraOptions || {},
		},
	];
};

const parseOptreeRow = function(ps, parseCellContents, extraOptions) {
	const connectList = [];
	let match;
	// Handle \connect calls
	while ((match = /^\s*\\connect/.exec(ps.getRest()))) {
		ps.pos += match[0].length;

		const connectInfo = parseConnect(ps, extraOptions);
		connectList.push(connectInfo);
	}

	// Parsing cell contents.
	const cellPs = Helpers.splitByDelimIntoPs(ps, {
		begin: "&",
		escape: "\\",
	});

	const cellNodes = cellPs.map(parseCellContents);
	const ignorePadding = [];
	cellNodes.forEach(cellNode => {
		if (
			!extraOptions.raw &&
			Helpers.findFirstDescendant(
				cellNode,
				node => noPaddingTypes.indexOf(node.type) !== -1
			)
		) {
			ignorePadding.push(true);
		} else {
			ignorePadding.push(false);
		}
	});

	return {
		type: "optreeRow",
		children: cellNodes,
		ignorePadding,
		connectList,
		extraOptions,
	};
};

const parseConnect = function(ps, extraOptions) {
	let errorPos = ps.pos + 1;

	let hidden = false;
	let optArg = Helpers.findOptionalArgument(ps);
	while (optArg) {
		if (!hidden && optArg.text === "hide") {
			hidden = true;
		} else {
			ps.pushError(
				optArg.text + " is not a valid optional argument to \\connect",
				errorPos
			);
			return false;
		}
		optArg = Helpers.findOptionalArgument(ps);
	}
	const args = Helpers.findArguments(ps, 2, {requireBrace: true});
	if (!args || args.length < 2) {
		ps.pushError(
			"\\connect must have two arguments, the first a semicolon separated list of numbers, and the second just a single number",
			errorPos
		);
		return false;
	}

	const prevRowText = args[0].getRest();
	const connectPrevRowRegex = /^\d+(;\d+)*$/;
	if (!connectPrevRowRegex.test(prevRowText)) {
		ps.pushError(
			"Invalid connect argument format; first argument should be '#;#;...;#'.",
			errorPos
		);
		return false;
	}
	errorPos += prevRowText.length;

	const currRowText = args[1].getRest();

	const connectCurRowRegex = /^\d+$/;
	if (!connectCurRowRegex.test(currRowText)) {
		ps.pushError(
			"Invalid connect argument format; second argument should be a number.",
			errorPos
		);
		return false;
	}

	const parseConnectArg = function(numText) {
		let num = parseInt(numText) - 1;
		if (!extraOptions.raw) {
			num = num * 2 + 1;
		}

		if (!_.isFinite(num) || num < 0) {
			ps.pushError(
				"Invalid connect argument format; arguments should be positive integers.",
				errorPos
			);
			return false;
		}

		return num;
	};

	const argParts = prevRowText.split(";");
	const prevCols = argParts.map(parseConnectArg);

	const currCol = parseConnectArg(currRowText);

	if (_.includes(prevCols, false) || currCol === false) {
		return false;
	}

	return {
		type: "connect",
		prevCols,
		currCol,
		hidden,
		extraOptions,
	};
};

module.exports = {
	parseOptree: parseOptree,
};
